/**
 * GoodGuardian — Child Wellbeing Log
 *
 * Daily check-ins: mood, health, school updates, sleep quality.
 * Timestamped records useful in custody hearings and CPS cases.
 */
import { useState, useMemo } from 'react'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { todayYMD } from '@/utils/dateUtils'
import { api as authApi } from '@/utils/auth'

// ── Config ────────────────────────────────────────────────────────────────────
const MOODS = [
  { value: 'great', emoji: '😄', label: 'Great',  bg: 'bg-green-100',  text: 'text-green-700',  border: 'border-green-400'  },
  { value: 'good',  emoji: '🙂', label: 'Good',   bg: 'bg-brand-100',  text: 'text-brand-700',  border: 'border-brand-400'  },
  { value: 'okay',  emoji: '😐', label: 'Okay',   bg: 'bg-slate-100',  text: 'text-slate-600',  border: 'border-slate-400'  },
  { value: 'upset', emoji: '😢', label: 'Upset',  bg: 'bg-amber-100',  text: 'text-amber-700',  border: 'border-amber-400'  },
  { value: 'angry', emoji: '😡', label: 'Angry',  bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-400' },
  { value: 'sick',  emoji: '🤒', label: 'Sick',   bg: 'bg-red-100',    text: 'text-red-600',    border: 'border-red-400'    },
]

const SLEEP = [
  { value: 'good', emoji: '😴', label: 'Good'  },
  { value: 'fair', emoji: '🥱', label: 'Fair'  },
  { value: 'poor', emoji: '😩', label: 'Poor'  },
]

const MOOD_MAP = Object.fromEntries(MOODS.map(m => [m.value, m]))

const EMPTY_FORM = {
  date: todayYMD(), child_id: '',
  mood: '', health: '', school: '', sleep: '', notes: '', flag: false,
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(ymd) {
  if (!ymd) return ''
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  const today = todayYMD()
  if (ymd === today) return 'Today'
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1)
  if (ymd === `${yesterday.getFullYear()}-${String(yesterday.getMonth()+1).padStart(2,'0')}-${String(yesterday.getDate()).padStart(2,'0')}`)
    return 'Yesterday'
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

// ── Add Entry Modal ───────────────────────────────────────────────────────────
function AddEntryModal({ onClose, onSave, children, defaultChildId }) {
  const [form, setForm]     = useState({ ...EMPTY_FORM, child_id: defaultChildId || '' })
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState(null)
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const canSave = form.date && form.mood

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    setError(null)
    try {
      await onSave({ ...form })
      onClose()
    } catch {
      setError('Could not save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div
        className="w-full max-w-lg mx-auto bg-white dark:bg-slate-800 rounded-t-3xl p-5 flex flex-col gap-5 max-h-[92vh] overflow-y-auto no-scrollbar animate-slide-up"
        role="dialog" aria-modal="true" aria-label="Add wellbeing entry"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-1 rounded-full bg-slate-200 dark:bg-slate-600 mx-auto -mt-1" />
        <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">New Wellbeing Check-in</h3>

        {/* Date + Child */}
        <div className={`grid gap-3 ${children?.length > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <Input label="Date" type="date" value={form.date}
            onChange={e => set('date', e.target.value)} required />
          {children?.length > 0 && (
            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">Child</label>
              <select value={form.child_id} onChange={e => set('child_id', e.target.value)}
                className="input-base text-sm">
                <option value="">All children</option>
                {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Mood — required */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-2">
            Mood <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {MOODS.map(m => (
              <button key={m.value} onClick={() => set('mood', m.value)}
                className={`flex flex-col items-center gap-1 py-3 rounded-2xl border-2 font-medium text-sm transition-all ${
                  form.mood === m.value
                    ? `${m.bg} ${m.text} ${m.border}`
                    : 'bg-white dark:bg-slate-700 text-slate-600 border-slate-200 dark:border-slate-600'
                }`}>
                <span className="text-2xl">{m.emoji}</span>
                <span className="text-xs font-semibold">{m.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Sleep quality */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-2">Sleep quality</label>
          <div className="flex gap-2">
            {SLEEP.map(s => (
              <button key={s.value} onClick={() => set('sleep', form.sleep === s.value ? '' : s.value)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                  form.sleep === s.value
                    ? 'bg-brand-100 text-brand-700 border-brand-400'
                    : 'bg-white dark:bg-slate-700 text-slate-600 border-slate-200 dark:border-slate-600'
                }`}>
                <span>{s.emoji}</span> {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Text fields */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            Health notes <span className="text-xs font-normal text-slate-400">(illness, injuries, medications)</span>
          </label>
          <textarea value={form.health} onChange={e => set('health', e.target.value)}
            rows={2} placeholder="e.g. slight fever, gave Children's Tylenol at 7pm"
            className="input-base resize-none text-sm" />
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            School update <span className="text-xs font-normal text-slate-400">(attendance, behavior, homework)</span>
          </label>
          <textarea value={form.school} onChange={e => set('school', e.target.value)}
            rows={2} placeholder="e.g. spelling test on Friday, note from teacher about lunch"
            className="input-base resize-none text-sm" />
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            Additional notes
          </label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
            rows={2} placeholder="Anything else worth recording…"
            className="input-base resize-none text-sm" />
        </div>

        {/* Court-relevant flag */}
        <label className="flex items-center gap-3 cursor-pointer bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-3 py-3">
          <input type="checkbox" checked={form.flag} onChange={e => set('flag', e.target.checked)}
            className="w-4 h-4 accent-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700 dark:text-red-400">🚩 Flag as court-relevant</p>
            <p className="text-xs text-red-500 dark:text-red-500 mt-0.5">Marks this entry for easy reference during hearings or CPS proceedings</p>
          </div>
        </label>

        {error && <p className="text-xs text-red-600 font-medium">{error}</p>}

        <div className="flex gap-3 pb-1">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1"
            loading={saving} disabled={!canSave} onClick={handleSave}>
            Save Entry
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Edit Entry Modal ─────────────────────────────────────────────────────────
function EditEntryModal({ log, onClose, onSave, children }) {
  const [form, setForm]     = useState({
    date:     log.date     || todayYMD(),
    child_id: log.child_id || '',
    mood:     log.mood     || '',
    health:   log.health   || '',
    school:   log.school   || '',
    sleep:    log.sleep    || '',
    notes:    log.notes    || '',
    flag:     !!log.flag,
  })
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState(null)
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const canSave = form.date && form.mood

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    setError(null)
    try {
      await onSave(log.id, { ...form })
      onClose()
    } catch {
      setError('Could not save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div
        className="w-full max-w-lg mx-auto bg-white dark:bg-slate-800 rounded-t-3xl p-5 flex flex-col gap-5 max-h-[92vh] overflow-y-auto no-scrollbar animate-slide-up"
        role="dialog" aria-modal="true" aria-label="Edit wellbeing entry"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-1 rounded-full bg-slate-200 dark:bg-slate-600 mx-auto -mt-1" />
        <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">Edit Check-in</h3>

        {/* Date + Child */}
        <div className={`grid gap-3 ${children?.length > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <Input label="Date" type="date" value={form.date}
            onChange={e => set('date', e.target.value)} required />
          {children?.length > 0 && (
            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">Child</label>
              <select value={form.child_id} onChange={e => set('child_id', e.target.value)}
                className="input-base text-sm">
                <option value="">All children</option>
                {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Mood — required */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-2">
            Mood <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {MOODS.map(m => (
              <button key={m.value} onClick={() => set('mood', m.value)}
                className={`flex flex-col items-center gap-1 py-3 rounded-2xl border-2 font-medium text-sm transition-all ${
                  form.mood === m.value
                    ? `${m.bg} ${m.text} ${m.border}`
                    : 'bg-white dark:bg-slate-700 text-slate-600 border-slate-200 dark:border-slate-600'
                }`}>
                <span className="text-2xl">{m.emoji}</span>
                <span className="text-xs font-semibold">{m.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Sleep quality */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-2">Sleep quality</label>
          <div className="flex gap-2">
            {SLEEP.map(s => (
              <button key={s.value} onClick={() => set('sleep', form.sleep === s.value ? '' : s.value)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                  form.sleep === s.value
                    ? 'bg-brand-100 text-brand-700 border-brand-400'
                    : 'bg-white dark:bg-slate-700 text-slate-600 border-slate-200 dark:border-slate-600'
                }`}>
                <span>{s.emoji}</span> {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Text fields */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            Health notes <span className="text-xs font-normal text-slate-400">(illness, injuries, medications)</span>
          </label>
          <textarea value={form.health} onChange={e => set('health', e.target.value)}
            rows={2} placeholder="e.g. slight fever, gave Children's Tylenol at 7pm"
            className="input-base resize-none text-sm" />
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            School update <span className="text-xs font-normal text-slate-400">(attendance, behavior, homework)</span>
          </label>
          <textarea value={form.school} onChange={e => set('school', e.target.value)}
            rows={2} placeholder="e.g. spelling test on Friday, note from teacher about lunch"
            className="input-base resize-none text-sm" />
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            Additional notes
          </label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
            rows={2} placeholder="Anything else worth recording…"
            className="input-base resize-none text-sm" />
        </div>

        {/* Court-relevant flag */}
        <label className="flex items-center gap-3 cursor-pointer bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-3 py-3">
          <input type="checkbox" checked={form.flag} onChange={e => set('flag', e.target.checked)}
            className="w-4 h-4 accent-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700 dark:text-red-400">🚩 Flag as court-relevant</p>
            <p className="text-xs text-red-500 dark:text-red-500 mt-0.5">Marks this entry for easy reference during hearings or CPS proceedings</p>
          </div>
        </label>

        {error && <p className="text-xs text-red-600 font-medium">{error}</p>}

        <div className="flex gap-3 pb-1">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1"
            loading={saving} disabled={!canSave} onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Entry Card ────────────────────────────────────────────────────────────────
function EntryCard({ log, onDelete, onEdit, childName }) {
  const mood = MOOD_MAP[log.mood] || MOOD_MAP.okay
  const sleep = SLEEP.find(s => s.value === log.sleep)

  return (
    <div className={`card flex flex-col gap-3 animate-slide-up relative ${log.flag ? 'border-l-4 border-red-400' : ''}`}>

      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <span className="text-2xl leading-none">{mood.emoji}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${mood.bg} ${mood.text}`}>{mood.label}</span>
              {sleep && (
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                  {sleep.emoji} {sleep.label} sleep
                </span>
              )}
              {log.flag && (
                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold">🚩 Flagged</span>
              )}
            </div>
            {childName && (
              <p className="text-xs text-slate-400 mt-0.5 font-medium">{childName}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onEdit(log)}
            className="tap-target text-slate-300 hover:text-brand-500 transition-colors"
            aria-label="Edit entry">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M15.232 5.232l3.536 3.536M9 13l6.071-6.071a2.5 2.5 0 013.536 3.536L12.536 16.5H9v-3.5z" />
            </svg>
          </button>
          <button onClick={() => onDelete(log.id)}
            className="tap-target text-slate-300 hover:text-red-400 transition-colors"
            aria-label="Delete entry">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Detail rows */}
      {log.health && (
        <div className="flex gap-2 items-start bg-red-50 dark:bg-red-900/10 rounded-xl px-3 py-2">
          <span className="text-sm flex-shrink-0">🏥</span>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{log.health}</p>
        </div>
      )}
      {log.school && (
        <div className="flex gap-2 items-start bg-brand-50 dark:bg-brand-900/10 rounded-xl px-3 py-2">
          <span className="text-sm flex-shrink-0">🎓</span>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{log.school}</p>
        </div>
      )}
      {log.notes && (
        <div className="flex gap-2 items-start">
          <span className="text-sm flex-shrink-0">📝</span>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{log.notes}</p>
        </div>
      )}

      {/* Timestamp */}
      <p className="text-xs text-slate-300 dark:text-slate-600">
        Logged {new Date(log.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
      </p>
    </div>
  )
}

// ── Screen ────────────────────────────────────────────────────────────────────
export default function WellbeingLog() {
  const { children, wellbeingLogs, addWellbeingLog, updateWellbeingLog, deleteWellbeingLog } = useApp()

  const [selectedChild, setSelectedChild] = useState('all')
  const [showForm, setShowForm]           = useState(false)
  const [editingLog, setEditingLog]       = useState(null)

  // Filter by selected child
  const filtered = useMemo(() =>
    selectedChild === 'all'
      ? wellbeingLogs
      : wellbeingLogs.filter(l => l.child_id === selectedChild),
    [wellbeingLogs, selectedChild]
  )

  // Group by date
  const grouped = useMemo(() => {
    const map = {}
    for (const log of filtered) {
      if (!map[log.date]) map[log.date] = []
      map[log.date].push(log)
    }
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a))
  }, [filtered])

  const childMap = useMemo(() =>
    Object.fromEntries((children || []).map(c => [c.id, c])),
    [children]
  )

  const flaggedCount = wellbeingLogs.filter(l => l.flag).length

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <AppHeader title="Wellbeing Log"
        right={
          <button onClick={() => setShowForm(true)}
            className="tap-target w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/80"
            aria-label="Add check-in">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        }
      />

      {/* Child filter tabs */}
      {children?.length > 0 && (
        <div className="flex border-b border-slate-100 dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-900 overflow-x-auto no-scrollbar"
          role="tablist" aria-label="Filter by child">
          {[{ id: 'all', name: 'All' }, ...children].map(c => (
            <button key={c.id} onClick={() => setSelectedChild(c.id)}
              role="tab" aria-selected={selectedChild === c.id}
              className={`flex-shrink-0 px-4 py-3 text-xs font-semibold transition-colors whitespace-nowrap ${
                selectedChild === c.id
                  ? 'text-brand-700 border-b-2 border-brand-700'
                  : 'text-slate-500'
              }`}>
              {c.id === 'all' ? 'All' : c.name}
            </button>
          ))}
        </div>
      )}

      <div className="screen no-scrollbar px-4 py-4 flex flex-col gap-1">

        {/* Stats row */}
        {wellbeingLogs.length > 0 && (
          <div className="flex gap-3 mb-3">
            <div className="card flex-1 text-center py-3">
              <p className="text-2xl font-black text-slate-800 dark:text-slate-100">{filtered.length}</p>
              <p className="text-xs text-slate-500">Total entries</p>
            </div>
            <div className="card flex-1 text-center py-3">
              <p className="text-2xl font-black text-slate-800 dark:text-slate-100">
                {filtered.filter(l => {
                  const d = new Date(); d.setDate(d.getDate() - 7)
                  return l.date >= d.toISOString().split('T')[0]
                }).length}
              </p>
              <p className="text-xs text-slate-500">Last 7 days</p>
            </div>
            {flaggedCount > 0 && (
              <div className="card flex-1 text-center py-3 border-red-200 dark:border-red-800">
                <p className="text-2xl font-black text-red-600">{flaggedCount}</p>
                <p className="text-xs text-red-500">🚩 Flagged</p>
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        {grouped.length === 0 ? (
          <div className="card bg-brand-50 dark:bg-brand-900/20 border border-brand-200 text-center py-12 flex flex-col items-center gap-3 mt-4">
            <span className="text-5xl">😊</span>
            <p className="font-bold text-slate-800 dark:text-slate-100">No check-ins yet</p>
            <p className="text-xs text-slate-500 max-w-xs">
              Log daily mood, health, and school updates. These timestamped records are valuable evidence in custody and CPS proceedings.
            </p>
            <Button variant="primary" size="md" onClick={() => setShowForm(true)}>
              Add First Check-in
            </Button>
          </div>
        ) : (
          grouped.map(([date, logs]) => (
            <div key={date} className="mb-4">
              {/* Date header */}
              <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white dark:bg-slate-900 py-1 z-10">
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {fmtDate(date)}
                </p>
                <div className="flex-1 h-px bg-slate-100 dark:bg-slate-700" />
                <p className="text-xs text-slate-400">{date}</p>
              </div>

              <div className="flex flex-col gap-3">
                {logs.map(log => (
                  <EntryCard
                    key={log.id}
                    log={log}
                    onDelete={deleteWellbeingLog}
                    onEdit={setEditingLog}
                    childName={log.child_id && children?.length > 1 ? childMap[log.child_id]?.name : null}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => setShowForm(true)}
        className="fixed bottom-20 right-4 w-14 h-14 brand-gradient rounded-full shadow-lg flex items-center justify-center text-white z-30 active:scale-95 transition-transform"
        aria-label="Add check-in">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {showForm && (
        <AddEntryModal
          onClose={() => setShowForm(false)}
          onSave={addWellbeingLog}
          children={children}
          defaultChildId={selectedChild !== 'all' ? selectedChild : ''}
        />
      )}

      {editingLog && (
        <EditEntryModal
          log={editingLog}
          onClose={() => setEditingLog(null)}
          onSave={updateWellbeingLog}
          children={children}
        />
      )}
    </div>
  )
}
